import {AuthModel, UserModel} from './_models'
import {toast} from 'react-toastify'
import {AxiosError, AxiosResponse} from 'axios'
import {storageKey} from './AuthContext'

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v'
const CURRENTUSER_LOCAL_STORAGE_KEY = 'kt-currentuser-react-v'
const getAuth = (): AuthModel | undefined => {
  if (!localStorage) {
    return
  }

  const lsValue: string | null = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY)
  if (!lsValue) {
    return
  }

  try {
    const auth: AuthModel = JSON.parse(lsValue) as AuthModel
    if (auth) {
      // You can easily check auth_token expiration also
      return auth
    }
  } catch (error) {
    console.error('AUTH LOCAL STORAGE PARSE ERROR', error)
  }
}
const getCurrentUser = (): UserModel | undefined => {
  if (!localStorage) {
    return
  }

  const lsValue: string | null = localStorage.getItem(CURRENTUSER_LOCAL_STORAGE_KEY)
  if (!lsValue) {
    return
  }

  try {
    const user: UserModel = JSON.parse(lsValue) as UserModel
    if (user) {
      return user
    }
  } catch (error) {
    console.error('AUTH LOCAL STORAGE PARSE ERROR', error)
  }
}
const setAuth = (auth: AuthModel) => {
  if (!localStorage) {
    return
  }

  try {
    const lsValue = JSON.stringify(auth)
    localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, lsValue)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE SAVE ERROR', error)
  }
}
const setCurrentUser = (currentUser: UserModel) => {
  if (!localStorage) {
    return
  }

  try {
    const lsValue = JSON.stringify(currentUser)
    localStorage.setItem(CURRENTUSER_LOCAL_STORAGE_KEY, lsValue)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE SAVE ERROR', error)
  }
}

const removeAuth = () => {
  if (!localStorage) {
    return
  }

  try {
    localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE REMOVE ERROR', error)
  }
}
const removeCurrentUser = () => {
  if (!localStorage) {
    return
  }

  try {
    localStorage.removeItem(CURRENTUSER_LOCAL_STORAGE_KEY)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE REMOVE ERROR', error)
  }
}
let isRefreshing = false
let failedQueue: {
  resolve: () => void
  reject: (reason?: any) => void
}[] = []
const processQueue = (error: any) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve()
    }
  })
  failedQueue = []
}
function setupAxios(axios: any, logout?: any, refresh?: any) {
  axios.defaults.headers.Accept = 'application/json'
  axios.interceptors.request.use(
    (config: {headers: {Authorization: string; business_id: string}}) => {
      const token = localStorage.getItem(storageKey.token)
      if (token) config.headers.Authorization = `Bearer ${token}`
      return config
    },
    (err: any) => Promise.reject(err)
  )
  axios.interceptors.response.use(
    (response: AxiosResponse) => {
      response.data?.message && toast.success(response.data.message)
      return response.data.data
    },
    async (error: AxiosError) => {
      if (process.env.NODE_ENV === 'development') console.log(error.response)
      const originalRequest: any = error.config
      if (error?.response?.status === 403 && !originalRequest._retry) {
        if (isRefreshing) {
          // Queue this request until token is refreshed
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: () => {
                resolve(axios(originalRequest))
              },
              reject: (err) => {
                reject(err)
              },
            })
          })
        }
        originalRequest._retry = true
        isRefreshing = true
        try {
          await refresh()
          processQueue(null)
          return axios(originalRequest)
        } catch (error) {
          processQueue(error)
          logout()
        } finally {
          isRefreshing = false
        }
      } else if (error?.response?.status === 401) {
        logout()
      } else {
        const data: any = error?.response?.data
        if (data?.message) toast.error(data.message)
        throw error
      }
    }
  )
}

export {
  getAuth,
  getCurrentUser,
  setAuth,
  setCurrentUser,
  removeAuth,
  removeCurrentUser,
  AUTH_LOCAL_STORAGE_KEY,
  setupAxios,
}
